import type { SubscriptionUsage } from '@/types/api/subscription'
import { formatLimitValue, formatUsageValue, numericLimit } from '@/utils/quota'

interface ProgressWidgetProps {
  title: string
  usage?: SubscriptionUsage
}

const COLORS = ['from-cyan-500 to-blue-500', 'from-emerald-500 to-teal-500', 'from-purple-500 to-violet-500', 'from-amber-500 to-orange-500']

export function ProgressWidget({ title, usage }: ProgressWidgetProps) {
  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-5 w-5 items-center justify-center rounded bg-gradient-to-br from-cyan-500 to-blue-500 text-[10px] text-white font-bold">▤</div>
        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{title}</p>
      </div>
      {usage?.usage?.length ? (
        <div className="flex flex-1 flex-col justify-center gap-4">
          {usage.usage.map((item, index) => {
            const limit = numericLimit(item.limitValue)
            const pct = Number.isFinite(limit) && limit > 0
              ? Math.min(100, Math.round((item.currentUsage / limit) * 100))
              : 0
            const gradient = COLORS[index % COLORS.length]
            return (
              <div key={item.featureKey}>
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-sm font-medium text-neutral-700">{item.name}</p>
                  <p className="text-xs text-neutral-500">
                    {formatUsageValue(item.featureKey, item.currentUsage)} / {formatLimitValue(item.featureKey, item.limitValue)}
                  </p>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-neutral-100 ring-1 ring-inset ring-neutral-200/50">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r ${gradient} transition-all duration-700 ease-out`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-neutral-400">No usage data available</p>
        </div>
      )}
    </div>
  )
}
